import { useEffect, useState } from 'react'
import { useAccount, usePublicClient } from 'wagmi'
import { ADDRESSES, IS_LIVE, ZERO_ADDR } from '@/lib/contracts'
import multiplyAbi from '@/lib/abis/MultiplyRouter.json'
import { getLogsChunked } from '@/lib/getLogsChunked'
import { toNumber } from '@/lib/utils'

function ev(name) { return multiplyAbi.find((x) => x.type === 'event' && x.name === name) }
const OPENED = ev('Opened')
const OPENED_ONE_STEP = ev('OpenedOneStep')
const CLOSED = ev('Closed')

const NOT_DEPLOYED = !IS_LIVE || (ADDRESSES.MULTIPLY_ROUTER || '').toLowerCase() === ZERO_ADDR

/// A user's cost basis and realised entry price for one loop, reconstructed from the router's own
/// events — no contract change and no indexer (CHANGELOG.md item 123).
///
/// **Cost basis resets on `Closed`.** `close()` fully unwinds: debt to zero, collateral returned.
/// So the basis is the sum of `principal` from opens AFTER the most recent close, not over all
/// history. Summing everything would keep counting a position the user already exited, and the
/// resulting P&L would drift further from reality with every round trip.
///
/// **Entry price is derived, not stored:**
///
///     entry price (debt per collateral) = totalDebtAdded / (totalCollateralAdded - principal)
///
/// which is the EXECUTED rate including the swap fee and slippage actually paid — a better basis
/// than an oracle mark, which records what the price was rather than what this position got.
///
/// Weighted across multiple opens by the amounts themselves rather than averaged naively: two opens
/// of very different size do not contribute equally to what the position cost.
export function useLoopPosition(loop) {
  const { address: user } = useAccount()
  const client = usePublicClient()
  const [state, setState] = useState({ loading: true, known: false })

  const collateralAsset = loop?.collateral?.asset
  const debtAsset = loop?.debt?.asset

  useEffect(() => {
    if (NOT_DEPLOYED || !client || !user || !collateralAsset || !debtAsset) {
      setState({ loading: false, known: false })
      return undefined
    }
    let cancelled = false
    setState({ loading: true, known: false })

    async function run() {
      try {
        const fromBlock = ADDRESSES.LENDING_POOL_DEPLOY_BLOCK
          ? BigInt(ADDRESSES.LENDING_POOL_DEPLOY_BLOCK)
          : 0n
        const args = { user, collateralAsset, debtAsset }
        const scan = (event) => (event
          ? getLogsChunked(client, { address: ADDRESSES.MULTIPLY_ROUTER, event, args, fromBlock })
          : Promise.resolve([]))

        const [opens, oneSteps, closes] = await Promise.all([
          scan(OPENED), scan(OPENED_ONE_STEP), scan(CLOSED),
        ])
        if (cancelled) return

        // One ordering across all three kinds - a close only invalidates the opens BEFORE it, so
        // the sequence matters and a per-kind view cannot express it.
        const ordered = [
          ...opens.map((l) => ({ ...l, kind: 'open' })),
          ...oneSteps.map((l) => ({ ...l, kind: 'open' })),
          ...closes.map((l) => ({ ...l, kind: 'close' })),
        ].sort((a, b) => (a.blockNumber === b.blockNumber
          ? Number(a.logIndex) - Number(b.logIndex)
          : (a.blockNumber > b.blockNumber ? 1 : -1)))

        let principalWei = 0n
        let collateralAddedWei = 0n
        let debtAddedWei = 0n
        let opensSinceClose = 0
        for (const l of ordered) {
          if (l.kind === 'close') {
            principalWei = 0n; collateralAddedWei = 0n; debtAddedWei = 0n; opensSinceClose = 0
            continue
          }
          principalWei += l.args.principal ?? 0n
          collateralAddedWei += l.args.totalCollateralAdded ?? 0n
          debtAddedWei += l.args.totalDebtAdded ?? 0n
          opensSinceClose++
        }

        if (opensSinceClose === 0) { if (!cancelled) setState({ loading: false, known: false }); return }

        const cDec = loop.collateral.decimals
        const dDec = loop.debt.decimals
        const principal = toNumber(principalWei, cDec)
        const collateralAdded = toNumber(collateralAddedWei, cDec)
        const debtAdded = toNumber(debtAddedWei, dDec)
        const bought = collateralAdded - principal

        if (!cancelled) {
          setState({
            loading: false,
            known: true,
            opens: opensSinceClose,
            principal,
            collateralAdded,
            debtAdded,
            // Null rather than Infinity when nothing was bought - at 1x there is no executed price
            // to report, and a division result would be a fabricated one.
            entryPrice: bought > 0 ? debtAdded / bought : null,
          })
        }
      } catch (e) {
        console.error('loop position scan failed:', e)
        if (!cancelled) setState({ loading: false, known: false, error: true })
      }
    }

    run()
    return () => { cancelled = true }
  }, [client, user, collateralAsset, debtAsset, loop?.collateral?.decimals, loop?.debt?.decimals])

  return state
}

// `computeLoopPnl` lives in lib/loops.js rather than here: it is pure arithmetic, and this module
// imports wagmi at the top, so anything defined here can only be tested by pulling the whole wallet
// stack into the test environment. Re-exported so callers still have one import site.
export { computeLoopPnl } from '@/lib/loops'
